import {useRef} from 'react'

/**
 * Beyond this depth values compare by reference only. Bounds the walk on
 * cyclic structures (anything self-referential would otherwise overflow the
 * stack) and on pathologically deep data; legitimate option lists are 2-3
 * levels deep.
 */
const MAX_DEPTH = 8

/**
 * Structural equality for plain data (primitives, arrays, plain objects,
 * Date). Values it can't introspect (functions, Map/Set, class instances,
 * React elements, anything past MAX_DEPTH) compare by reference only — for
 * this hook that's the safe direction: a false "not equal" merely keeps the
 * fresh reference and re-renders.
 */
const deepEqual = (a: unknown, b: unknown, depth = 0): boolean => {
  if (a === b) return true
  if (a === null || b === null || typeof a !== 'object' || typeof b !== 'object') {
    return Number.isNaN(a as number) && Number.isNaN(b as number)
  }
  if (depth >= MAX_DEPTH) return false
  // React elements (e.g. JSX labels inside select options) are never walked:
  // in dev they carry a circular fiber reference via _owner, and two separate
  // renders never produce identical elements anyway.
  if ((a as any).$$typeof !== undefined || (b as any).$$typeof !== undefined) return false
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false
    return a.every((item, i) => deepEqual(item, b[i], depth + 1))
  }
  if (a instanceof Date || b instanceof Date) {
    return a instanceof Date && b instanceof Date && a.getTime() === b.getTime()
  }
  const keysA = Object.keys(a)
  const keysB = Object.keys(b)
  if (keysA.length !== keysB.length) return false
  return keysA.every(
    (key) =>
      Object.prototype.hasOwnProperty.call(b, key) &&
      deepEqual((a as Record<string, unknown>)[key], (b as Record<string, unknown>)[key], depth + 1)
  )
}

/**
 * Returns the previous reference whenever the incoming value is deep-equal to
 * it. Callers often rebuild structurally-identical arrays/objects on every
 * render (e.g. `options={list.map(...)}`); keeping the old reference lets
 * downstream useMemo/React.memo boundaries hold.
 */
export function useDeepStable<T>(value: T): T {
  const ref = useRef(value)
  if (value !== ref.current && !deepEqual(value, ref.current)) {
    ref.current = value
  }
  return ref.current
}

export default useDeepStable
